import React, { useState, useEffect } from "react";
import "react-quill/dist/quill.snow.css";
import OptionEditor from "../../components/editor/MyQuillEditor";
import { getGroups, addQuestion } from "../../api";
import toast, { Toaster } from 'react-hot-toast';
import MyQuillEditor from "../../components/editor/MyQuillEditor";
import { 
  PlusCircleIcon, 
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const AddQuestion = () => {
  const [questionGroups, setQuestionGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [title, setTitle] = useState("");
  const [optionsCount, setOptionsCount] = useState("");
  const [options, setOptions] = useState([]);
  const [correctOption, setCorrectOption] = useState(null);
  const [marks, setMarks] = useState("");
  const [toption, setToption] = useState("");

  // Validation state
  const [errors, setErrors] = useState({
    title: "",
    group: "",
    marks: "",
    options: "",
    correctOption: "",
  });

  useEffect(() => {
    fetchQuestionGroups();
  }, []);

  useEffect(() => {
    if (optionsCount !== "") {
      setOptions(Array(parseInt(optionsCount)).fill(""));
    } else {
      setOptions([]);
    }
  }, [optionsCount]);

  useEffect(() => {
    if (toption !== "") {
      setOptions(Array(parseInt(toption)).fill(""));
    }
  }, [toption]);

  const fetchQuestionGroups = async () => {
    const data = await getGroups();
    setQuestionGroups(data);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleOptionEditorChange = (index, value) => {
    handleOptionChange(index, value);
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      title: "",
      group: "",
      marks: "",
      options: "",
      correctOption: "",
    };

    if (!title.trim()) {
      newErrors.title = "Question title is required.";
      valid = false;
    }

    if (!selectedGroup) {
      newErrors.group = "Please select a question group.";
      valid = false;
    }

    if (!marks || isNaN(marks) || marks <= 0) {
      newErrors.marks = "Marks must be a positive number.";
      valid = false;
    }

    if (options.some(option => option.trim() === "")) {
      newErrors.options = "All options must be filled in.";
      valid = false;
    }

    if (correctOption === null) {
      newErrors.correctOption = "Correct option must be selected.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      question: title,
      group: selectedGroup,
      marks: marks,
      toption: toption,
      options: options.map((option, index) => ({ 
        name: option, 
        isCorrect: correctOption === index 
      })),
    };

    console.log("Payload to send:", payload);

    try {
      const response = await addQuestion(payload);
      console.log("Response from API:", response);
      toast.success('Question added successfully!');
      handleReset();
    } catch (error) {
      console.error("Error adding question:", error);
      toast.error('Failed to add question. Please try again.');
    }
  };

  const handleReset = () => {
    setTitle("");
    setSelectedGroup("");
    setMarks("");
    setOptions([]);
    setCorrectOption(null);
    setToption("");
    setErrors({
      title: "",
      group: "",
      marks: "",
      options: "",
      correctOption: "",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <PlusCircleIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Add New Question</h1>
              <p className="text-sm text-gray-500">Create and manage exam questions</p>
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          
          {/* Question Title Section */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Question Title <span className="text-red-500">*</span>
            </label>
            <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
              <MyQuillEditor
                theme="snow"
                value={title}
                onChange={setTitle}
                className="quill-editor"
                placeholder="Enter your question here..."
              />
            </div>
            {errors.title && (
              <div className="flex items-center mt-2 text-red-600 text-sm">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.title}
              </div>
            )}
          </div>

          {/* Question Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            
            {/* Question Group */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Question Group <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.group ? 'border-red-500' : 'border-gray-200'
                }`}
              >
                <option value="">Select Group</option>
                {questionGroups.map((group) => (
                  <option key={group.gid} value={group.gid}>
                    {group.Title}
                  </option>
                ))}
              </select>
              {errors.group && (
                <div className="flex items-center mt-2 text-red-600 text-sm">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.group}
                </div>
              )}
            </div>

            {/* Marks */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Marks <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={marks}
                onChange={(e) => setMarks(e.target.value)}
                className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.marks ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="Enter marks"
                min="1"
              />
              {errors.marks && (
                <div className="flex items-center mt-2 text-red-600 text-sm">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.marks}
                </div>
              )}
            </div>

            {/* Number of Options */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Number of Options <span className="text-red-500">*</span>
              </label>
              <select
                value={toption}
                onChange={(e) => setToption(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              >
                <option value="">Select Options</option>
                {[3, 4, 5, 6, 7].map((count) => (
                  <option key={count} value={count}>
                    {count} Options
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Options Section */}
          {options.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-semibold text-gray-700">
                  Answer Options <span className="text-red-500">*</span>
                </label>
                {errors.correctOption && (
                  <div className="flex items-center text-red-600 text-sm">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.correctOption}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {options.map((option, index) => (
                  <div 
                    key={index} 
                    className={`bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 p-5 transition-all ${
                      correctOption === index 
                        ? 'border-green-500 shadow-md' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* Option Header */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="flex items-center space-x-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                          correctOption === index 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-700'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span className="text-sm font-medium text-gray-600">
                          Option {String.fromCharCode(65 + index)}
                        </span>
                      </span>

                      {/* Correct Answer Radio */}
                      <label className="flex items-center cursor-pointer group">
                        <input
                          type="radio"
                          name="correctOption"
                          checked={correctOption === index}
                          onChange={() => setCorrectOption(index)}
                          className="w-5 h-5 text-green-600 border-gray-300 focus:ring-green-500"
                        />
                        <span className={`ml-2 text-xs font-medium ${
                          correctOption === index ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {correctOption === index ? 'Correct ✓' : 'Mark as correct'}
                        </span>
                      </label>
                    </div>

                    {/* Option Editor */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <OptionEditor
                        value={option}
                        onChange={(value) => handleOptionEditorChange(index, value)}
                        style={{ minHeight: "100px" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              {errors.options && (
                <div className="flex items-center mt-3 text-red-600 text-sm">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.options}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
            <button 
              onClick={handleCreate} 
              className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3.5 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all shadow-md hover:shadow-lg"
            >
              <CheckCircleIcon className="h-5 w-5" />
              <span>Create Question</span>
            </button>
            
            <button 
              onClick={handleReset} 
              className="flex-1 flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-6 py-3.5 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300"
            >
              <ArrowPathIcon className="h-5 w-5" />
              <span>Reset Form</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuestion;
